const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';

export interface ParagraphNumberingRef {
  numId: string;
  ilvl: number;
}

export interface ParagraphSpacing {
  beforeTwips: number | null;
  afterTwips: number | null;
}

export interface TableCellFidelity {
  widthTwips: number | null;
  borders: Partial<Record<'top' | 'right' | 'bottom' | 'left', string>>;
  shadingFill: string | null;
}

export interface TableFidelity {
  columnWidthsTwips: number[];
  rows: TableCellFidelity[][];
}

export interface PageGeometry {
  // All values in twips (1 twip = 1/1440 inch = 1/20 pt)
  pageWidthTwips: number;
  pageHeightTwips: number;
  marginTopTwips: number;
  marginBottomTwips: number;
  marginLeftTwips: number;
  marginRightTwips: number;
}

export interface ImageDimension {
  widthPt: number;
  heightPt: number;
}

export interface DocxXmlModel {
  paragraphNumbering: Array<ParagraphNumberingRef | null>;
  paragraphSpacing: ParagraphSpacing[];
  tables: TableFidelity[];
  runIsLastRenderedPageBreakMarker: boolean[];
  paragraphHasSectionPageBreak: boolean[];
  pageGeometry: PageGeometry;
  imageDimensions: Array<ImageDimension | null>;
  runParagraphStyleId: (string | null)[];
}

function localName(el: Element): string {
  return el.localName || el.tagName.replace(/^.*:/, '');
}

function getAttr(el: Element, name: string): string | null {
  return el.getAttributeNS(W_NS, name) || el.getAttribute(`w:${name}`) || el.getAttribute(name);
}

function children(el: Element, name: string): Element[] {
  const result: Element[] = [];
  for (let i = 0; i < el.childNodes.length; i++) {
    const node = el.childNodes[i];
    if (node.nodeType === 1 && localName(node as Element) === name) {
      result.push(node as Element);
    }
  }
  return result;
}

function firstChild(el: Element, name: string): Element | null {
  const found = children(el, name);
  return found.length ? found[0] : null;
}

interface RawCell {
  colSpan: number;
  vMergeContinue: boolean;
  widthTwips: number | null;
  borders: TableCellFidelity['borders'];
  shadingFill: string | null;
  rowSpan: number;
}

export function parseDocxXmlModel(documentXml: string): DocxXmlModel {
  const doc = new DOMParser().parseFromString(documentXml, 'application/xml');
  const parseError = doc.getElementsByTagName('parsererror')[0];
  if (parseError) {
    throw new Error('Failed to parse word/document.xml: ' + parseError.textContent);
  }

  const body = firstChild(doc.documentElement, 'body');

  const paragraphNumbering: Array<ParagraphNumberingRef | null> = [];
  const paragraphSpacing: ParagraphSpacing[] = [];
  const tables: TableFidelity[] = [];
  const runIsLastRenderedPageBreakMarker: boolean[] = [];
  const paragraphHasSectionPageBreak: boolean[] = [];
  const imageDimensions: Array<ImageDimension | null> = [];
  const runParagraphStyleId: (string | null)[] = [];

  // mammoth recurses into <w:ins> (those runs count in its run index) but ignores <w:del>
  // entirely, and strips <w:lastRenderedPageBreak/> from every run it sees, including ones
  // inside <w:ins>. So <w:ins> runs must stay in this index but are marked withinIns=true,
  // forcing them to never be treated as page-break markers.
  // mammoth's body-reader.js reads <w:pict> via `readChildElements(element).toExtra()`:
  // the paragraphs/runs inside a VML drawing (e.g. a text box) are NOT folded into the
  // current paragraph's own runs - they become separate Paragraph nodes spliced in as
  // siblings right after the paragraph that contains the drawing (see collectTextboxContent
  // below, which mirrors that same splicing for our own paragraph-indexed arrays). So
  // <w:pict> must be skipped here, or its nested runs would be double-counted as if they
  // belonged to this paragraph, corrupting every run-index-based array from that point on.
  function collectRunElements(container: Element): { run: Element; withinIns: boolean }[] {
    const result: { run: Element; withinIns: boolean }[] = [];
    function walk(el: Element, withinIns: boolean): void {
      for (let i = 0; i < el.childNodes.length; i++) {
        const node = el.childNodes[i];
        if (node.nodeType !== 1) {
          continue;
        }
        const child = node as Element;
        const name = localName(child);
        if (name === 'del' || name === 'pict') {
          continue;
        }
        if (name === 'AlternateContent') {
          // mammoth reads ONLY <mc:Fallback>, ignoring <mc:Choice> entirely.
          const fallback = firstChild(child, 'Fallback');
          if (fallback) {
            walk(fallback, withinIns);
          }
          continue;
        }
        if (name === 'r') {
          result.push({ run: child, withinIns });
        }
        walk(child, withinIns || (name === 'ins'));
      }
    }
    walk(container, false);
    return result;
  }

  // Finds <w:p>/<w:tbl> nested inside VML text boxes (<w:pict> > ... > <v:textbox> >
  // <w:txbxContent>) anywhere within `root`, in document order. mammoth's generic
  // element recursion walks into w:pict/v:shape/v:roundrect/v:rect/v:group/v:textbox/
  // w:txbxContent looking for ordinary content, and (via insertExtra) splices what it
  // finds in as siblings immediately after the paragraph containing the drawing. This
  // mirrors that so our paragraph-indexed arrays land on the same paragraph mammoth does.
  // Modern DrawingML shapes (<w:drawing>) are deliberately NOT recursed into here:
  // mammoth's wp:inline/wp:anchor reader only extracts images, never shape text.
  function collectTextboxContent(root: Element): Element[] {
    const found: Element[] = [];
    function walk(el: Element): void {
      for (let i = 0; i < el.childNodes.length; i++) {
        const node = el.childNodes[i];
        if (node.nodeType !== 1) {
          continue;
        }
        const child = node as Element;
        const name = localName(child);
        if (name === 'del') {
          continue;
        }
        if (name === 'AlternateContent') {
          const fallback = firstChild(child, 'Fallback');
          if (fallback) {
            walk(fallback);
          }
          continue;
        }
        if (name === 'txbxContent') {
          for (let j = 0; j < child.childNodes.length; j++) {
            const inner = child.childNodes[j];
            if (inner.nodeType !== 1) {
              continue;
            }
            const innerName = localName(inner as Element);
            if (innerName === 'p' || innerName === 'tbl') {
              found.push(inner as Element);
            }
          }
          continue;
        }
        walk(child);
      }
    }
    walk(root);
    return found;
  }

  // mammoth's default image converter (images.dataUri) only ever sets `src` and `alt`
  // on the resulting <img> - it never carries over the DOCX's own size, so a browser
  // falls back to the image file's intrinsic pixel size, which is very often much
  // larger than the size Word actually displayed it at (Word scales images down via
  // this size metadata without re-encoding the file). Finds that size for every image
  // in `root`, in the same document order mammoth's own convertImage callback fires,
  // so the two can be zipped together by position later:
  //   - modern DrawingML: <w:drawing> > ... > <wp:extent cx="…" cy="…"/> (EMUs)
  //   - legacy VML (e.g. <w:pict> from "Compatibility Mode" docs): <v:shape
  //     style="width:…pt;height:…pt">
  // A `<w:drawing>`/`<w:pict>` wrapper is not recursed into further (images don't
  // nest inside images), but if it wraps more than one image, its size is repeated
  // once per image found inside - matching how many times mammoth would call back.
  const EMU_PER_POINT = 12700;

  function findDescendant(el: Element, name: string): Element | null {
    for (let i = 0; i < el.childNodes.length; i++) {
      const node = el.childNodes[i];
      if (node.nodeType !== 1) {
        continue;
      }
      const child = node as Element;
      if (localName(child) === name) {
        return child;
      }
      const found = findDescendant(child, name);
      if (found) {
        return found;
      }
    }
    return null;
  }

  function countDescendants(el: Element, name: string): number {
    let count = 0;
    for (let i = 0; i < el.childNodes.length; i++) {
      const node = el.childNodes[i];
      if (node.nodeType !== 1) {
        continue;
      }
      const child = node as Element;
      if (localName(child) === name) {
        count += 1;
      }
      count += countDescendants(child, name);
    }
    return count;
  }

  function parseVmlStyleDimensionPt(style: string | null, prop: 'width' | 'height'): number | null {
    if (!style) {
      return null;
    }
    const match = new RegExp(`${prop}\\s*:\\s*([\\d.]+)pt`, 'i').exec(style);
    return match ? parseFloat(match[1]) : null;
  }

  function collectImageDimensions(root: Element): void {
    for (let i = 0; i < root.childNodes.length; i++) {
      const node = root.childNodes[i];
      if (node.nodeType !== 1) {
        continue;
      }
      const child = node as Element;
      const name = localName(child);

      if (name === 'drawing') {
        const extentEl = findDescendant(child, 'extent');
        const cx = extentEl ? parseInt(extentEl.getAttribute('cx') || '', 10) : NaN;
        const cy = extentEl ? parseInt(extentEl.getAttribute('cy') || '', 10) : NaN;
        const dim: ImageDimension | null =
          Number.isFinite(cx) && Number.isFinite(cy) ? { widthPt: cx / EMU_PER_POINT, heightPt: cy / EMU_PER_POINT } : null;
        const blipCount = Math.max(countDescendants(child, 'blip'), 1);
        for (let b = 0; b < blipCount; b++) {
          imageDimensions.push(dim);
        }
        continue;
      }

      if (name === 'pict') {
        const shapeEl = findDescendant(child, 'shape');
        const style = shapeEl ? shapeEl.getAttribute('style') : null;
        const widthPt = parseVmlStyleDimensionPt(style, 'width');
        const heightPt = parseVmlStyleDimensionPt(style, 'height');
        const dim: ImageDimension | null = widthPt !== null && heightPt !== null ? { widthPt, heightPt } : null;
        const imagedataCount = Math.max(countDescendants(child, 'imagedata'), 1);
        for (let b = 0; b < imagedataCount; b++) {
          imageDimensions.push(dim);
        }
        continue;
      }

      collectImageDimensions(child);
    }
  }

  function isPureLastRenderedPageBreakRun(r: Element): boolean {
    if (!firstChild(r, 'lastRenderedPageBreak')) {
      return false;
    }
    // .trim() treats whitespace-only <w:t> text (e.g. xml:space="preserve" + a space) as
    // empty - such runs are still pure markers.
    const hasText = children(r, 't').some((t) => (t.textContent || '').trim().length > 0);
    return !hasText;
  }

  function readParagraph(p: Element): void {
    const pPr = firstChild(p, 'pPr');
    let numbering: ParagraphNumberingRef | null = null;
    let spacing: ParagraphSpacing = { beforeTwips: null, afterTwips: null };
    let hasSectionPageBreak = false;
    let pStyleId: string | null = null;

    if (pPr) {
      const pStyleEl = firstChild(pPr, 'pStyle');
      pStyleId = pStyleEl ? getAttr(pStyleEl, 'val') : null;

      const numPr = firstChild(pPr, 'numPr');
      if (numPr) {
        const numIdEl = firstChild(numPr, 'numId');
        const ilvlEl = firstChild(numPr, 'ilvl');
        const numId = numIdEl ? getAttr(numIdEl, 'val') : null;
        if (numId) {
          numbering = { numId, ilvl: ilvlEl ? parseInt(getAttr(ilvlEl, 'val') || '0', 10) : 0 };
        }
      }

      const spacingEl = firstChild(pPr, 'spacing');
      if (spacingEl) {
        const before = getAttr(spacingEl, 'before');
        const after = getAttr(spacingEl, 'after');
        spacing = {
          beforeTwips: before ? parseInt(before, 10) : null,
          afterTwips: after ? parseInt(after, 10) : null,
        };
      }

      const sectPr = firstChild(pPr, 'sectPr');
      if (sectPr) {
        const typeEl = firstChild(sectPr, 'type');
        const sectionType = typeEl ? getAttr(typeEl, 'val') : null;
        hasSectionPageBreak = sectionType !== 'continuous';
      }
    }

    paragraphNumbering.push(numbering);
    paragraphSpacing.push(spacing);
    paragraphHasSectionPageBreak.push(hasSectionPageBreak);

    collectRunElements(p).forEach(({ run, withinIns }) => {
      runIsLastRenderedPageBreakMarker.push(withinIns ? false : isPureLastRenderedPageBreakRun(run));
      // Word resolves a run's effective font/size from its OWN direct formatting first,
      // then its character style, then falls back to the ENCLOSING PARAGRAPH's style (e.g.
      // a paragraph styled "Heading1" with a locally-redefined 10pt Arial supplies the size
      // for any run inside it that doesn't set w:sz itself). mammoth only ever reads a run's
      // own direct w:sz/w:rFonts, so this paragraph-style id is threaded through per-run to
      // let the fidelity transform fall back to it (see resolveRunFontByStyleId).
      runParagraphStyleId.push(pStyleId);
    });

    // Mirror mammoth's insertExtra splicing (see collectRunElements/collectTextboxContent
    // comments above): any text box content found in this paragraph's runs is processed
    // immediately as if it were the next sibling paragraph/table.
    collectTextboxContent(p).forEach((node) => {
      const name = localName(node);
      if (name === 'p') {
        readParagraph(node);
      } else if (name === 'tbl') {
        readTable(node);
      }
    });
  }

  function readCellFormatting(tc: Element): { colSpan: number; vMergeContinue: boolean; widthTwips: number | null; borders: TableCellFidelity['borders']; shadingFill: string | null } {
    const tcPr = firstChild(tc, 'tcPr');
    let colSpan = 1;
    let vMergeContinue = false;
    let widthTwips: number | null = null;
    const borders: TableCellFidelity['borders'] = {};
    let shadingFill: string | null = null;

    if (tcPr) {
      const gridSpanEl = firstChild(tcPr, 'gridSpan');
      if (gridSpanEl) {
        colSpan = parseInt(getAttr(gridSpanEl, 'val') || '1', 10) || 1;
      }

      const vMergeEl = firstChild(tcPr, 'vMerge');
      if (vMergeEl) {
        const val = getAttr(vMergeEl, 'val');
        vMergeContinue = val === 'continue' || !val;
      }

      const tcW = firstChild(tcPr, 'tcW');
      if (tcW) {
        const w = getAttr(tcW, 'w');
        widthTwips = w ? parseInt(w, 10) : null;
      }

      const tcBorders = firstChild(tcPr, 'tcBorders');
      if (tcBorders) {
        (['top', 'left', 'bottom', 'right'] as const).forEach((side) => {
          const sideEl = firstChild(tcBorders, side);
          if (sideEl) {
            const val = getAttr(sideEl, 'val');
            const sz = getAttr(sideEl, 'sz');
            const color = getAttr(sideEl, 'color');
            if (val && val !== 'nil' && val !== 'none') {
              const widthPx = sz ? Math.max(1, Math.round(parseInt(sz, 10) / 8)) : 1;
              const cssColor = color && color !== 'auto' ? `#${color}` : '#000';
              borders[side] = `${widthPx}px solid ${cssColor}`;
            }
          }
        });
      }

      const shd = firstChild(tcPr, 'shd');
      if (shd) {
        const fill = getAttr(shd, 'fill');
        if (fill && fill !== 'auto' && fill.toUpperCase() !== 'FFFFFF') {
          shadingFill = fill;
        }
      }
    }

    return { colSpan, vMergeContinue, widthTwips, borders, shadingFill };
  }

  function readTable(tbl: Element): void {
    const tblGrid = firstChild(tbl, 'tblGrid');
    const columnWidthsTwips = tblGrid
      ? children(tblGrid, 'gridCol').map((col) => parseInt(getAttr(col, 'w') || '0', 10))
      : [];

    const rawRows: RawCell[][] = [];
    const cellElementsByRow: Element[][] = [];

    children(tbl, 'tr').forEach((tr) => {
      const rowCells: RawCell[] = [];
      const rowCellElements: Element[] = [];
      children(tr, 'tc').forEach((tc) => {
        const formatting = readCellFormatting(tc);
        rowCells.push({ ...formatting, rowSpan: 1 });
        rowCellElements.push(tc);
      });
      rawRows.push(rowCells);
      cellElementsByRow.push(rowCellElements);
    });

    // mammoth's calculateRowSpans (mammoth/lib/docx/body-reader.js) removes vertical-merge
    // continuation cells from the tree entirely as part of the initial document read -
    // BEFORE transformDocument (and its paragraph index) ever runs. So this bookkeeping
    // pass must run, and dropped cells must be identified, BEFORE any paragraph content is
    // read below - otherwise those cells' paragraphs get counted here but never visited by
    // mammoth's transform, permanently desyncing every paragraph-indexed array after them.
    const columns: Record<number, RawCell> = {};
    const isDropped: boolean[][] = rawRows.map((row) => row.map(() => false));
    rawRows.forEach((row, rowIndex) => {
      let cellIndex = 0;
      row.forEach((cell, cellIndexInRow) => {
        if (cell.vMergeContinue && columns[cellIndex]) {
          columns[cellIndex].rowSpan++;
          isDropped[rowIndex][cellIndexInRow] = true;
        } else {
          columns[cellIndex] = cell;
          cell.vMergeContinue = false;
        }
        cellIndex += cell.colSpan;
      });
    });

    cellElementsByRow.forEach((rowCellElements, rowIndex) => {
      rowCellElements.forEach((tc, cellIndexInRow) => {
        if (isDropped[rowIndex][cellIndexInRow]) {
          return;
        }
        for (let i = 0; i < tc.childNodes.length; i++) {
          const node = tc.childNodes[i];
          if (node.nodeType !== 1) {
            continue;
          }
          const name = localName(node as Element);
          if (name === 'p') {
            readParagraph(node as Element);
          } else if (name === 'tbl') {
            readTable(node as Element);
          }
        }
      });
    });

    const rows: TableCellFidelity[][] = rawRows.map((row) =>
      row
        .filter((cell) => !cell.vMergeContinue)
        .map((cell) => ({
          widthTwips: cell.widthTwips,
          borders: cell.borders,
          shadingFill: cell.shadingFill,
        }))
    );

    tables.push({ columnWidthsTwips, rows });
  }

  let pageGeometry: PageGeometry = {
    pageWidthTwips: 11906,
    pageHeightTwips: 16838,
    marginTopTwips: 1134,
    marginBottomTwips: 1134,
    marginLeftTwips: 1134,
    marginRightTwips: 1134,
  };

  if (body) {
    for (let i = 0; i < body.childNodes.length; i++) {
      const node = body.childNodes[i];
      if (node.nodeType !== 1) {
        continue;
      }
      const name = localName(node as Element);
      if (name === 'p') {
        readParagraph(node as Element);
      } else if (name === 'tbl') {
        readTable(node as Element);
      } else if (name === 'sectPr') {
        // Body-level sectPr — read page size and margins
        const sectPr = node as Element;
        const pgSz = firstChild(sectPr, 'pgSz');
        const pgMar = firstChild(sectPr, 'pgMar');

        const w = pgSz ? getAttr(pgSz, 'w') : null;
        const h = pgSz ? getAttr(pgSz, 'h') : null;
        const top = pgMar ? getAttr(pgMar, 'top') : null;
        const bottom = pgMar ? getAttr(pgMar, 'bottom') : null;
        const left = pgMar ? getAttr(pgMar, 'left') : null;
        const right = pgMar ? getAttr(pgMar, 'right') : null;

        pageGeometry = {
          pageWidthTwips: w ? parseInt(w, 10) : 11906,
          pageHeightTwips: h ? parseInt(h, 10) : 16838,
          marginTopTwips: top ? parseInt(top, 10) : 1134,
          marginBottomTwips: bottom ? parseInt(bottom, 10) : 1134,
          marginLeftTwips: left ? parseInt(left, 10) : 1134,
          marginRightTwips: right ? parseInt(right, 10) : 1134,
        };
      }
    }
    collectImageDimensions(body);
  }

  return {
    paragraphNumbering,
    paragraphSpacing,
    tables,
    runIsLastRenderedPageBreakMarker,
    paragraphHasSectionPageBreak,
    pageGeometry,
    imageDimensions,
    runParagraphStyleId,
  };
}
